'use client';

import { useEffect, useRef, useState } from 'react';

const CONTROL_BUTTON =
  'inline-flex h-12 flex-1 items-center justify-center gap-2 px-3.5 text-xs font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-white sm:h-11 sm:flex-none sm:rounded-full sm:border sm:border-white/25 sm:bg-primary/80 sm:shadow-lg sm:backdrop-blur sm:hover:bg-primary sm:focus-visible:outline-offset-2';

function SoundIcon({ muted }: { muted: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z" />
      {muted ? (
        <>
          <path d="m16 9 5 6" />
          <path d="m21 9-5 6" />
        </>
      ) : (
        <>
          <path d="M15.5 9.5a4 4 0 0 1 0 5" />
          <path d="M18 7a7.5 7.5 0 0 1 0 10" />
        </>
      )}
    </svg>
  );
}

export default function HomepageDemoVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const manuallyPaused = useRef(false);
  const isIntersecting = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 639px)');
    const syncFormat = () => setIsMobile(media.matches);

    syncFormat();
    media.addEventListener('change', syncFormat);
    return () => media.removeEventListener('change', syncFormat);
  }, []);

  const source = isMobile
    ? '/demo/homepage/india-case-status-product-demo-mobile-music.mp4?v=20260711-upbeat'
    : '/demo/homepage/india-case-status-product-demo-music.mp4?v=20260711-upbeat';
  const poster = isMobile
    ? '/demo/homepage/poster-mobile.webp?v=20260711-polished'
    : '/demo/homepage/poster.webp?v=20260711-polished';

  useEffect(() => {
    const video = videoRef.current;
    const wrapper = wrapperRef.current;
    if (!video || !wrapper) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      video.pause();
      return;
    }

    const syncPlayback = () => {
      if (
        isIntersecting.current &&
        !manuallyPaused.current &&
        !document.hidden
      ) {
        video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersecting.current = entry.isIntersecting;
        syncPlayback();
      },
      { threshold: 0.35 },
    );

    observer.observe(wrapper);
    document.addEventListener('visibilitychange', syncPlayback);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
    };
  }, [source]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      manuallyPaused.current = false;
      video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      manuallyPaused.current = true;
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  return (
    <div
      ref={wrapperRef}
      className="group relative overflow-hidden rounded-2xl border border-primary/15 bg-primary shadow-[0_36px_90px_-28px_rgba(30,58,95,0.52),0_18px_36px_-20px_rgba(30,58,95,0.34)]"
    >
      <video
        ref={videoRef}
        key={source}
        className="block aspect-square h-auto w-full bg-primary object-cover sm:aspect-video"
        src={source}
        muted={isMuted}
        loop
        playsInline
        preload="metadata"
        poster={poster}
        aria-label="India Case Status product walkthrough showing one Cause List, complete case details, attached order PDFs, automatic alerts, and the hearing calendar"
        aria-describedby="homepage-demo-description"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
      />

      <p id="homepage-demo-description" className="sr-only">
        The visual tour starts with one searchable Cause List, opens an active matter and its
        complete case file, shows attached official order PDFs and automatic WhatsApp and email
        alerts, then finishes on the hearing calendar and a Track 1 case free call to action.
      </p>

      <div className="relative flex h-12 w-full border-t border-white/15 bg-primary sm:absolute sm:bottom-5 sm:right-5 sm:h-auto sm:w-auto sm:gap-2 sm:border-0 sm:bg-transparent">
        <button
          type="button"
          onClick={togglePlayback}
          className={CONTROL_BUTTON}
          aria-label={isPlaying ? 'Pause product demo' : 'Play product demo'}
        >
          {isPlaying ? (
            <>
              <span className="flex gap-1" aria-hidden="true"><span className="h-3.5 w-0.5 bg-white" /><span className="h-3.5 w-0.5 bg-white" /></span>
              <span className="sm:hidden">Pause demo</span>
              <span className="hidden sm:inline">Pause</span>
            </>
          ) : (
            <>
              <span className="ml-0.5 text-base leading-none" aria-hidden="true">▶</span>
              <span className="sm:hidden">Play demo</span>
              <span className="hidden sm:inline">Play</span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={toggleSound}
          className={`${CONTROL_BUTTON} border-l border-white/15 sm:border`}
          aria-label={isMuted ? 'Turn on demo music' : 'Mute demo music'}
          aria-pressed={!isMuted}
        >
          <SoundIcon muted={isMuted} />
          <span className="sm:hidden">{isMuted ? 'Sound on' : 'Mute music'}</span>
          <span className="hidden sm:inline">{isMuted ? 'Sound' : 'Mute'}</span>
        </button>
      </div>
    </div>
  );
}
